import React, { useState } from 'react'
import type { PresentationIndustry, QuoteFile, PresentationLibrary } from 'paintscout'
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  ConfirmationDialog,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  makeStyles
} from '@paintscout/ui'
import { FileDropArea, useClientOptions, useDialogs, useFileDropArea, useUser } from '@paintscout/ui-business'
import type { DialogProps, MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { CloseButton } from '../../../common'
import { PresentationFilesGrid, StockPhotoPanel } from '../../components'
import PublishIcon from '@material-ui/icons/Publish'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { addFilesToLibrary, getFeature, removeFileFromLibrary, getLibraryFileKey, uuid } from '@paintscout/util/builder'
import { PRESENTATION_LABEL_PLURAL, PRESENTATION_LABEL } from '@paintscout/util'
import { useFlags } from 'shared/components/common/FlagsProvider'
import classnames from 'classnames'

type LibraryTab = 'library' | 'pexels'

const useStyles = makeStyles<Theme, PresentationLibraryDialogProps>(
  (theme) => ({
    root: {},
    paper: {
      height: '100vh'
    },
    tooltipRoot: {
      display: 'inline-block',
      marginLeft: theme.spacing(3)
    },
    tooltipBadge: {
      cursor: 'pointer',
      top: 0,
      right: 0,
      '& svg': {
        fontSize: '1rem',
        color: theme.palette.grey['700']
      }
    },
    tabsRoot: {
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(0, 2),
      alignItems: 'flex-end',
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0, 2.75)
      }
    },
    pexelsContent: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden'
    },
    preContent: {
      padding: theme.spacing(0, 4, 1),
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0, 3, 1)
      }
    },
    dropAreaRootEmpty: {
      height: '100%'
    },
    dropAreaRoot: {
      minHeight: '100%'
    },
    filesGridRoot: {
      marginTop: -7
    },
    noResultsBlock: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      height: '100%',
      justifyContent: 'center'
    }
  }),
  {
    name: 'PresentationLibraryDialog'
  }
)

export interface PresentationLibraryDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  presentationLibrary: {
    addFilesToLibrary: (args: { files: QuoteFile[] }) => void
    library: PresentationLibrary
    saveLibrary: (args: { library: PresentationLibrary }) => void
  }
  handleSelect?: (newFile: QuoteFile) => void
  onCancel: (event?: React.MouseEvent<HTMLElement>) => void
  /** Industry (or other seed) used for the initial Pexels search. */
  presentationIndustry?: PresentationIndustry
  /** When re-opening for an existing stock photo, the query that originally found it. */
  specificQuery?: string
}

export default function PresentationLibraryDialog(props: PresentationLibraryDialogProps) {
  const classes = useStyles(props)
  const { onCancel, handleSelect, presentationLibrary, presentationIndustry, specificQuery, ...baseDialogProps } = props
  const { library: initialLibrary, saveLibrary } = presentationLibrary
  const [library, setLibrary] = useState(initialLibrary)
  const [isDirty, setIsDirty] = useState(false)
  const [tab, setTab] = useState<LibraryTab>('library')
  const { openDialog, dismissDialog } = useDialogs()
  const { s3MediaUpload } = useFlags()
  const { options } = useClientOptions()
  const { isSuperadmin } = useUser()
  const hasStockPhotos = getFeature({ options, path: 'presentation.stockPhotos' }) || isSuperadmin
  const columns = useMediaQuery('md') ? 4 : 6

  const files = library?.order.map((key) => {
    return library.values[key].file
  })
  const hasFiles = files?.length > 0

  const handleUploadFiles = (files: QuoteFile[]) => {
    setIsDirty(true)
    setLibrary(addFilesToLibrary({ files, library }))
  }

  const handleRemoveFile = (index: number) => {
    const file = files[index]
    openDialog(ConfirmationDialog, {
      message: `Are you sure you want to remove this item from your library? Existing ${PRESENTATION_LABEL_PLURAL} won't be affected.`,
      onConfirm: () => {
        setIsDirty(true)
        setLibrary(removeFileFromLibrary({ file, library }))
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  const handleReorderFiles = (files: QuoteFile[]) => {
    const newOrder = files.map(getLibraryFileKey)
    setIsDirty(true)
    setLibrary({ ...library, order: newOrder })
  }

  // Any close path — the X button, or selecting a file/photo to fill a slot — must flush a
  // dirty library first, or uploads/reorders/removals made earlier in this session (isDirty)
  // are silently lost since the dialog never saves on unmount otherwise.
  const closeAndSaveIfDirty = () => {
    if (isDirty) saveLibrary({ library })
    onCancel()
  }

  const handleSelectFile = (index: number) => {
    handleSelect({ ...files[index], key: uuid() })
    closeAndSaveIfDirty()
  }

  // With a target slot to fill (handleSelect), a picked stock photo goes straight there, just
  // like picking an existing library item. Without one (e.g. opened from the sidebar for
  // library management only), it's added to the library instead and the tab switches back so
  // the user can see it landed.
  const handlePexelsSelect = (newFile: QuoteFile) => {
    if (handleSelect) {
      handleSelect(newFile)
      closeAndSaveIfDirty()
    } else {
      setIsDirty(true)
      setLibrary(addFilesToLibrary({ files: [newFile], library }))
      setTab('library')
    }
  }

  const { open, getInputProps } = useFileDropArea({
    multiple: true,
    onUpload: handleUploadFiles,
    s3MediaUploadEnabled: s3MediaUpload
  })

  const handleClose = () => {
    closeAndSaveIfDirty()
  }

  return (
    <Dialog {...baseDialogProps} classes={{ paper: classes.paper }} fullWidth maxWidth={'xl'}>
      <input {...getInputProps()} />
      <DialogTitle rightContent={<CloseButton onCancel={handleClose} />}>
        {hasStockPhotos ? 'Select an Image' : 'Library'}
        <span className={classes.tooltipRoot}>
          <Tooltip
            placement={'right'}
            content={
              tab === 'pexels'
                ? 'Search free stock photos to use in your ' + PRESENTATION_LABEL_PLURAL + '.'
                : `Add your most used images, videos, and pdfs to easily access them in any of your ${PRESENTATION_LABEL_PLURAL}.`
            }
            icon={<InfoOutlinedIcon />}
            classes={{ badge: classes.tooltipBadge }}
          />
        </span>
      </DialogTitle>
      {hasStockPhotos && (
        <Tabs
          value={tab}
          onChange={(_, value: LibraryTab) => setTab(value)}
          textColor={'primary'}
          indicatorColor={'primary'}
          classes={{ root: classes.tabsRoot }}
        >
          <Tab value={'library'} label={'Library'} />
          <Tab value={'pexels'} label={'Pexels'} />
        </Tabs>
      )}
      {tab === 'pexels' ? (
        <DialogContent classes={{ root: classes.pexelsContent }}>
          <StockPhotoPanel
            defaultQuery={presentationIndustry}
            specificQuery={specificQuery}
            onSelect={handlePexelsSelect}
          />
        </DialogContent>
      ) : (
        <DialogContent>
          <FileDropArea
            classes={{
              root: classnames({ [classes.dropAreaRoot]: hasFiles, [classes.dropAreaRootEmpty]: !hasFiles })
            }}
            onUpload={(files) => handleUploadFiles(files)}
            s3MediaUploadEnabled={s3MediaUpload}
          >
            {!hasFiles ? (
              <div className={classes.noResultsBlock}>
                <Typography variant={'overline'}>{'Your Library is empty'}</Typography>
                <Typography
                  variant={'body1'}
                >{`Add an item from your ${PRESENTATION_LABEL} or upload a file directly.`}</Typography>
                <Button startIcon={<PublishIcon />} onClick={() => open()} variant={'outlined'}>
                  {'Upload a file'}
                </Button>
              </div>
            ) : (
              <PresentationFilesGrid
                classes={{ root: classes.filesGridRoot }}
                columns={columns}
                editable
                files={files}
                isEditable
                onDelete={handleRemoveFile}
                onFileClick={handleSelect ? handleSelectFile : undefined}
                onReorder={handleReorderFiles}
                onUploadClick={() => open()}
                showDetails={false}
                filePreviewProps={{ showFirstPageOnly: true, fancyHover: true }}
              />
            )}
          </FileDropArea>
        </DialogContent>
      )}
    </Dialog>
  )
}
